import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useRef } from 'react';
import { useAuth0 } from '@brighte/passwordless-authenticator';
import { Stack } from '@spark-web/stack';
import { VerticalStepper } from '@spark-web/vertical-stepper';
import PageTitle from '../../components/PageTitle/PageTitle';
import ApplicationUnderReviewStep from '../../components/Step/ApplicationUnderReviewStep';
import Step from '../../components/Step/Step';
import { SESSION_TEARDOWN_DEADLINE } from '../../utils';
import WithdrawnApplication from '../SubmittedApplication/WithdrawnApplication';
import CustomerPortalCard from './CustomerPortalCard';
import DeclinedApplication from './DeclinedApplication';
import { useGetNextStepIndex, useGetStatusSteps } from './hooks';
import { ApplicationSubmissionStatus, StatusStep, StepType } from './types';

const CUSTOMER_PORTAL_ELIGIBLE_STATUSES = new Set([
  ApplicationSubmissionStatus.SUBMITTED,
  ApplicationSubmissionStatus.REFERRED,
  ApplicationSubmissionStatus.APPROVED,
]);

type NextStepsProps = {
  readonly status: ApplicationSubmissionStatus;
  readonly isStatusSettled: boolean;
};

function NextSteps({ status, isStatusSettled }: NextStepsProps) {
  const steps = useGetStatusSteps(status);
  const activeStep = useGetNextStepIndex(steps, status);
  const { clearCredentials } = useAuth0();
  const flags = useFlags();
  const showCustomerPortalAction =
    (flags['redirect-customer-to-customer-portal'] ?? false) && CUSTOMER_PORTAL_ELIGIBLE_STATUSES.has(status);

  const hasClearedSession = useRef(false);
  // clearCredentials gets a new identity every render (its useCallback closes over a fresh
  // LocalStorageCache), so it can't be an effect dependency without re-firing the effect in a
  // loop. Reading it through a ref keeps the teardown on the latest cache key without that churn.
  const clearCredentialsRef = useRef(clearCredentials);
  clearCredentialsRef.current = clearCredentials;

  const clearSessionOnce = useCallback(() => {
    if (hasClearedSession.current) {
      return;
    }
    hasClearedSession.current = true;
    clearCredentialsRef.current();
  }, []);

  // CT-3287: gated on isStatusSettled rather than firing on mount. Clearing the session empties
  // the token cache that the status query resolves lazily at request time, so an unconditional
  // mount-time clear sends that request out unauthenticated and shows a spurious "session expired"
  // dialog. For instant-approval products this defers the clear until the polling timer is done.
  useEffect(() => {
    if (isStatusSettled) {
      clearSessionOnce();
    }
  }, [isStatusSettled, clearSessionOnce]);

  // CT-3287 backstop: the status query can stay unsettled indefinitely — a hung request keeps
  // `isFetching` true, as does react-query's retry backoff — and the teardown above would then
  // never run, leaving credentials in local storage. Tear down unconditionally once the deadline
  // passes; it sits well past the polling window, so it only fires for a genuinely stuck request.
  useEffect(() => {
    const deadlineTimeout = setTimeout(clearSessionOnce, SESSION_TEARDOWN_DEADLINE * 1000);

    return () => clearTimeout(deadlineTimeout);
  }, [clearSessionOnce]);

  if (status === ApplicationSubmissionStatus.WITHDRAWN) {
    return <WithdrawnApplication />;
  }

  if (status === ApplicationSubmissionStatus.DECLINED) {
    return <DeclinedApplication />;
  }
  return (
    <>
      <PageTitle status={status} />
      <VerticalStepper activeIndex={activeStep}>
        {steps.map((step: StepType) =>
          step.key === StatusStep.APPLICATION_UNDER_REVIEW ? (
            <ApplicationUnderReviewStep key={step.key} />
          ) : (
            <Step {...step} key={step.key} />
          ),
        )}
      </VerticalStepper>
      {showCustomerPortalAction && (
        <Stack paddingTop="large">
          <CustomerPortalCard applicationStatus={status} />
        </Stack>
      )}
    </>
  );
}

export default NextSteps;
